import type { MapStyleValue } from "@/components/constants"
import type { LayerTypeValue, OpportunityType } from "@/model/DataModel"
import type { Filters } from "@/model/RootStore"
import type { SearchType } from "@/types/searchType"
import type { AuthenticationProvider } from "@/utils/authenticationProvider"

type ValueChange<Value> = {
	from: Value
	to: Value
}

type SelectFilterValue = (string | number | null)[] | string | number | null

type RangeFilterChange = ValueChange<[number, number]>

type DateRange = {
	start: Date | null
	end: Date | null
}

type AnalyticsUser = {
	id: string
	email?: string
	name: string
	role: string
}

const booleanFilterParentLayers = {
	endangeredSpecies: "endangered-species",
	transitLinesStatus: "transit-lines",
	transitStationsStatus: "transit-stations",
	planningPolicy: "planning-policy",
} as const

export type BooleanLayerFilterGroup = keyof typeof booleanFilterParentLayers
export type BooleanLayerFilterKey<Group extends BooleanLayerFilterGroup = BooleanLayerFilterGroup> =
	Group extends BooleanLayerFilterGroup ? keyof Filters[Group] & string : never
export type DevPotentialKey = keyof Filters["developmentPotential"] & string
export type UrbanPriorityKey = keyof Filters["urbanPriorities"] & string
export type UrbanPriorityStatus = keyof Filters["urbanPriorities"][UrbanPriorityKey] & string

const opportunityInteractionActions = {
	copyLink: true,
	like: true,
	dislike: true,
	createTarget: true,
	googleMaps: true,
	listingPage: true,
	hubspot: true,
	contract: true,
	contact: true,
	addComment: true,
	deleteTarget: true,
	pipelineStage: true,
	transfer: true,
	makeCcv: true,
	priority: true,
} as const

type OpportunityInteractionAction = keyof typeof opportunityInteractionActions
export type OpportunityInteractionChangeAction = "pipelineStage" | "priority"
export type OpportunityInteractionSimpleAction = Exclude<
	OpportunityInteractionAction,
	"addComment" | OpportunityInteractionChangeAction | "transfer"
>

type OpportunityInteractionBase = {
	opportunityId: number
}

type OpportunityInteraction =
	| (OpportunityInteractionBase & { action: OpportunityInteractionSimpleAction })
	| (OpportunityInteractionBase & { action: "addComment"; comment: string })
	| (OpportunityInteractionBase & { action: OpportunityInteractionChangeAction; from: string; to: string })
	| (OpportunityInteractionBase & { action: "transfer"; to: string })

export enum AnalyticsEventName {
	IdentifyUser = "identify_user",
	CaptureException = "capture_exception",
	ResetUser = "reset_user",
	SignInInitiated = "sign_in_initiated",
	UserSignedIn = "user_signed_in",
	UserSignedOut = "user_signed_out",
	BuyersListExported = "buyers_list_exported",
	NavigationClicked = "navigation_clicked",
	OpportunitySelected = "opportunity_selected",
	SearchInteracted = "search_interacted",
	LayerChanged = "layer_changed",
	LayerSelectionChanged = "layer_selection_changed",
	BooleanLayerFilterChanged = "boolean_layer_filter_changed",
	UrbanPriorityChanged = "urban_priority_changed",
	UrbanPriorityStatusChanged = "urban_priority_status_changed",
	DevelopmentPotentialChanged = "development_potential_changed",
	BaseMapChanged = "base_map_changed",
	FilterChanged = "filter_changed",
	RangeFilterChanged = "range_filter_changed",
	DateFilterChanged = "date_filter_changed",
	SavedSearchCreated = "saved_search_created",
	OpportunityInteracted = "opportunity_interacted",
}

type AnalyticsEventPayloads = {
	[AnalyticsEventName.IdentifyUser]: AnalyticsUser
	[AnalyticsEventName.CaptureException]: unknown
	[AnalyticsEventName.ResetUser]: undefined
	[AnalyticsEventName.SignInInitiated]: { provider: AuthenticationProvider }
	[AnalyticsEventName.UserSignedIn]: { provider: AuthenticationProvider }
	[AnalyticsEventName.UserSignedOut]: undefined
	[AnalyticsEventName.BuyersListExported]: { userId: string | null }
	[AnalyticsEventName.NavigationClicked]: { translationKey: string }
	[AnalyticsEventName.OpportunitySelected]: { id: string | number; opportunityType: OpportunityType }
	[AnalyticsEventName.SearchInteracted]: { searchType: SearchType; value: string }
	[AnalyticsEventName.LayerChanged]: { layerTypeValue: LayerTypeValue; active: boolean }
	[AnalyticsEventName.LayerSelectionChanged]: { layer: string; active: boolean }
	[AnalyticsEventName.BooleanLayerFilterChanged]: {
		group: BooleanLayerFilterGroup
		filter: BooleanLayerFilterKey
		active: boolean
	}
	[AnalyticsEventName.UrbanPriorityChanged]: { priority: UrbanPriorityKey; active: boolean }
	[AnalyticsEventName.UrbanPriorityStatusChanged]: {
		priority: UrbanPriorityKey
		status: UrbanPriorityStatus
		active: boolean
	}
	[AnalyticsEventName.DevelopmentPotentialChanged]: { potential: DevPotentialKey; active: boolean }
	[AnalyticsEventName.BaseMapChanged]: { style: MapStyleValue }
	[AnalyticsEventName.FilterChanged]: {
		filterTranslationKeys: string | string[]
		change: ValueChange<SelectFilterValue>
	}
	[AnalyticsEventName.RangeFilterChanged]: { filterTranslationKey: string; change: RangeFilterChange }
	[AnalyticsEventName.DateFilterChanged]: { from: DateRange; to: DateRange }
	[AnalyticsEventName.SavedSearchCreated]: { name: string; userId: string | null }
	[AnalyticsEventName.OpportunityInteracted]: OpportunityInteraction
}

export type AnalyticsEvent = {
	[Name in AnalyticsEventName]: {
		name: Name
		payload: AnalyticsEventPayloads[Name]
	}
}[AnalyticsEventName]

type AnalyticsEventListener = (event: AnalyticsEvent) => void

const listeners = new Set<AnalyticsEventListener>()

function publish(event: AnalyticsEvent): void {
	listeners.forEach(listener => listener(event))
}

function subscribe(listener: AnalyticsEventListener): () => void {
	listeners.add(listener)

	return () => listeners.delete(listener)
}

export const analytics = {
	subscribe,
	identifyUser(payload: AnalyticsUser): void {
		publish({ name: AnalyticsEventName.IdentifyUser, payload })
	},
	captureException(error: unknown): void {
		publish({ name: AnalyticsEventName.CaptureException, payload: error })
	},
	resetUser(): void {
		publish({ name: AnalyticsEventName.ResetUser, payload: undefined })
	},
	signInInitiated(payload: AnalyticsEventPayloads[AnalyticsEventName.SignInInitiated]): void {
		publish({ name: AnalyticsEventName.SignInInitiated, payload })
	},
	userSignedIn(payload: AnalyticsEventPayloads[AnalyticsEventName.UserSignedIn]): void {
		publish({ name: AnalyticsEventName.UserSignedIn, payload })
	},
	userSignedOut(): void {
		publish({ name: AnalyticsEventName.UserSignedOut, payload: undefined })
		publish({ name: AnalyticsEventName.ResetUser, payload: undefined })
	},
	buyersListExported(payload: AnalyticsEventPayloads[AnalyticsEventName.BuyersListExported]): void {
		publish({ name: AnalyticsEventName.BuyersListExported, payload })
	},
	navigationClicked(translationKey: string): void {
		publish({ name: AnalyticsEventName.NavigationClicked, payload: { translationKey } })
	},
	opportunitySelected(id: string | number, opportunityType: OpportunityType): void {
		publish({ name: AnalyticsEventName.OpportunitySelected, payload: { id, opportunityType } })
	},
	searchInteracted(searchType: SearchType, value: string): void {
		publish({ name: AnalyticsEventName.SearchInteracted, payload: { searchType, value } })
	},
	baseMapChanged(style: MapStyleValue): void {
		publish({ name: AnalyticsEventName.BaseMapChanged, payload: { style } })
	},
	layerChanged(layerTypeValue: LayerTypeValue, active: boolean): void {
		publish({ name: AnalyticsEventName.LayerChanged, payload: { layerTypeValue, active } })
	},
	layerSelectionChanged(layer: string, active: boolean): void {
		publish({ name: AnalyticsEventName.LayerSelectionChanged, payload: { layer, active } })
	},
	booleanLayerFilterChanged<Group extends BooleanLayerFilterGroup>(
		group: Group,
		filter: BooleanLayerFilterKey<Group>,
		active: boolean
	): void {
		publish({
			name: AnalyticsEventName.BooleanLayerFilterChanged,
			payload: { group, filter: filter as BooleanLayerFilterKey, active },
		})
	},
	urbanPriorityChanged(priority: UrbanPriorityKey, active: boolean): void {
		publish({ name: AnalyticsEventName.UrbanPriorityChanged, payload: { priority, active } })
	},
	urbanPriorityStatusChanged(priority: UrbanPriorityKey, status: UrbanPriorityStatus, active: boolean): void {
		publish({ name: AnalyticsEventName.UrbanPriorityStatusChanged, payload: { priority, status, active } })
	},
	developmentPotentialChanged(potential: DevPotentialKey, active: boolean): void {
		publish({ name: AnalyticsEventName.DevelopmentPotentialChanged, payload: { potential, active } })
	},
	filterChanged(filterTranslationKeys: string | string[], change: ValueChange<SelectFilterValue>): void {
		publish({ name: AnalyticsEventName.FilterChanged, payload: { filterTranslationKeys, change } })
	},
	rangeFilterChanged(filterTranslationKey: string, change: RangeFilterChange): void {
		publish({ name: AnalyticsEventName.RangeFilterChanged, payload: { filterTranslationKey, change } })
	},
	dateFilterChanged(from: DateRange, to: DateRange): void {
		publish({ name: AnalyticsEventName.DateFilterChanged, payload: { from, to } })
	},
	savedSearchCreated(payload: AnalyticsEventPayloads[AnalyticsEventName.SavedSearchCreated]): void {
		publish({ name: AnalyticsEventName.SavedSearchCreated, payload })
	},
	opportunityInteracted(payload: OpportunityInteraction): void {
		publish({ name: AnalyticsEventName.OpportunityInteracted, payload })
	},
}
